import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Matches `PADDING_LEFT` in `TableContentCell` / `TableSkeleton`. */
export const TABLE_CELL_HORIZONTAL_PADDING = 8;
export const DEFAULT_TABLE_COLUMN_WIDTH = 160;
export const DEFAULT_FIRST_TABLE_COLUMN_WIDTH = 320;
/**
 * Narrowest inner width a column can be dragged to, and the floor a persisted
 * width must meet to be restored. One constant so a width the user can drag to
 * is never discarded on reload. A column's `minWidth` overrides it.
 */
export const MIN_TABLE_COLUMN_WIDTH = 60;

export function getDefaultTableColumnWidth(
  isFirstColumn: boolean,
  width?: number,
  minWidth?: number,
): number {
  if (!isFirstColumn) {
    return width ?? minWidth ?? DEFAULT_TABLE_COLUMN_WIDTH;
  }

  if (width == null) {
    return Math.max(DEFAULT_FIRST_TABLE_COLUMN_WIDTH, minWidth ?? 0);
  }

  return Math.max(
    Math.min(width, DEFAULT_FIRST_TABLE_COLUMN_WIDTH),
    minWidth ?? 0,
  );
}

export function getTableColumnOuterWidth(innerWidth: number): number {
  return innerWidth + TABLE_CELL_HORIZONTAL_PADDING;
}

type PersistedWidths = Partial<Record<string, number>>;

function readKey(key: string): PersistedWidths | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return undefined;
    return parsed as PersistedWidths;
  } catch {
    return undefined;
  }
}

const STORAGE_VERSION = "v4";
const LEGACY_STORAGE_VERSION = "v3";

/**
 * Reads `<prefix>:v4`, falling back to the `:v3` widths saved before columns
 * filled the row. A v3 width on the `growableColumn` was a floor for a column
 * that kept growing, not a fixed width, so it is dropped; every other v3 width
 * already meant the column's exact width and carries over.
 */
function readPersisted(
  keyPrefix: string | undefined,
  legacyGrowthFloorColumn?: string,
): PersistedWidths {
  if (!keyPrefix || typeof window === "undefined") return {};
  const current = readKey(`${keyPrefix}:${STORAGE_VERSION}`);
  if (current) return current;
  const legacy = readKey(`${keyPrefix}:${LEGACY_STORAGE_VERSION}`);
  if (!legacy) return {};
  if (legacyGrowthFloorColumn != null) {
    const rest = { ...legacy };
    delete rest[legacyGrowthFloorColumn];
    return rest;
  }
  return legacy;
}

function mergeWidths<K extends string>(
  allColumns: readonly K[],
  defaults: Record<K, number>,
  mins: Partial<Record<K, number>>,
  persisted: PersistedWidths,
): { widths: Record<K, number>; resized: Set<K> } {
  const widths = {} as Record<K, number>;
  const resized = new Set<K>();
  for (const col of allColumns) {
    const d = defaults[col] ?? DEFAULT_TABLE_COLUMN_WIDTH;
    const floor = mins[col] ?? MIN_TABLE_COLUMN_WIDTH;
    const p = persisted[col as string];
    if (typeof p === "number" && Number.isFinite(p) && p >= floor) {
      widths[col] = p;
      resized.add(col);
    } else {
      widths[col] = d;
    }
  }
  return { widths, resized };
}

/**
 * Per-column inner widths (same units as `TableContentCell` `width` prop).
 *
 * When `persistenceKey` is set, widths are persisted to localStorage keyed by
 * `table-column-widths:<persistenceKey>:v4` (migrating `:v3`, see
 * `readPersisted`; pass the table's `growableColumn` as
 * `legacyGrowthFloorColumn`), and cross-tab updates are synced
 * via the `storage` event. Resizing writes the final width atomically on
 * pointer-up; there is no intermediate "drag override" React state (live drag
 * visuals are handled by direct DOM manipulation in `Table.tsx`).
 *
 * `resizedColumns` lists the columns whose width came from a drag (persisted
 * or session); horizontal-scroll tables keep those fixed instead of growing.
 */
export function useColumnWidths<K extends string>(
  persistenceKey: string | undefined,
  allColumns: readonly K[],
  defaults: Record<K, number>,
  mins: Partial<Record<K, number>>,
  legacyGrowthFloorColumn?: K,
): {
  widths: Record<K, number>;
  resizedColumns: ReadonlySet<K>;
  commitWidth: (col: K, width: number) => void;
} {
  const keyPrefix = persistenceKey
    ? `table-column-widths:${persistenceKey}`
    : undefined;
  const storageKey = keyPrefix ? `${keyPrefix}:${STORAGE_VERSION}` : undefined;
  // Only read when (re)loading, so a changing value never re-triggers a load.
  const legacyColumnRef = useRef<string | undefined>(legacyGrowthFloorColumn);
  legacyColumnRef.current = legacyGrowthFloorColumn;

  const [persisted, setPersisted] = useState<PersistedWidths>(() =>
    readPersisted(keyPrefix, legacyColumnRef.current),
  );

  // Reload when the storage key changes (e.g. persistenceKey bump to reset).
  const lastStorageKeyRef = useRef<string | undefined>(storageKey);
  useEffect(() => {
    if (lastStorageKeyRef.current !== storageKey) {
      lastStorageKeyRef.current = storageKey;
      setPersisted(readPersisted(keyPrefix, legacyColumnRef.current));
    }
  }, [keyPrefix, storageKey]);

  // Sync across tabs/windows so resizing in one window reflects in others.
  useEffect(() => {
    if (!storageKey) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      if (e.storageArea && e.storageArea !== window.localStorage) return;
      try {
        setPersisted(e.newValue ? JSON.parse(e.newValue) ?? {} : {});
      } catch {
        setPersisted({});
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  // Session-only fallback when no persistenceKey is provided.
  const [sessionWidths, setSessionWidths] = useState<PersistedWidths>({});

  const { widths, resized } = useMemo(
    () =>
      mergeWidths(
        allColumns,
        defaults,
        mins,
        storageKey ? persisted : sessionWidths,
      ),
    [allColumns, defaults, mins, persisted, sessionWidths, storageKey],
  );

  const commitWidth = useCallback(
    (col: K, width: number) => {
      const floor = mins[col] ?? MIN_TABLE_COLUMN_WIDTH;
      const clamped = Math.max(floor, width);
      if (!storageKey) {
        setSessionWidths((prev) => ({ ...prev, [col as string]: clamped }));
        return;
      }
      // Read–modify–write to localStorage synchronously. This is the source of
      // truth; React state is just a mirror for rendering. Writing outside of
      // a setState updater guarantees the write happens exactly once per call
      // (setState updaters can run twice under StrictMode).
      const current = readPersisted(keyPrefix, legacyColumnRef.current);
      const next: PersistedWidths = { ...current, [col as string]: clamped };
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // ignore quota / privacy-mode errors; widths stay in-memory this session
      }
      setPersisted(next);
    },
    [keyPrefix, mins, storageKey],
  );

  return { widths, resizedColumns: resized, commitWidth };
}
